/**
 * Formula group date range — same UI/UX as SMSWiser Stats DateRangePresetDropdown.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';
import { cn } from '@/lib/utils';
import { formatDateDMY } from '@/features/smswiser-campaigns/utils/dateFormat';
import {
  getPresetFromRange,
  getRangeFromPreset,
  getStatsDateRangeLabel,
  STATS_DATE_RANGE_PRESETS,
} from '@/components/date-range';
import type { StatsDateRangePreset } from '@/components/date-range';

import { formulaGroupToFromTo } from './formula-group-from-to.util';

export interface FormulaGroupDateRangeDropdownProps {
  dateRange: string;
  dateRangeCustomFrom?: string;
  dateRangeCustomTo?: string;
  onCommit: (patch: {
    dateRange: string;
    dateRangeCustomFrom?: string;
    dateRangeCustomTo?: string;
  }) => void;
  /** Preset codes to offer (default: every stats preset but custom). */
  presets?: ReadonlyArray<string>;
}

const ALL_PRESETS = STATS_DATE_RANGE_PRESETS.filter((p) => p.code !== 'custom');

const FormulaGroupDateRangeDropdown: FC<FormulaGroupDateRangeDropdownProps> = ({
  dateRange,
  dateRangeCustomFrom,
  dateRangeCustomTo,
  onCommit,
  presets,
}) => {
  const PRESETS = useMemo(
    () => ALL_PRESETS.filter((p) => !presets || presets.includes(p.code)),
    [presets],
  );
  const { from: fromDate, to: toDate } = useMemo(
    () => formulaGroupToFromTo({ dateRange, dateRangeCustomFrom, dateRangeCustomTo }),
    [dateRange, dateRangeCustomFrom, dateRangeCustomTo],
  );
  const [open, setOpen] = useState(false);
  const [localFrom, setLocalFrom] = useState(fromDate);
  const [localTo, setLocalTo] = useState(toDate);

  useEffect(() => {
    if (open) {
      setLocalFrom(fromDate);
      setLocalTo(toDate);
    }
  }, [open, fromDate, toDate]);

  const display = useMemo(() => {
    if (dateRange === 'custom' && dateRangeCustomFrom && dateRangeCustomTo) {
      return `${formatDateDMY(dateRangeCustomFrom)} → ${formatDateDMY(dateRangeCustomTo)}`;
    }
    return getStatsDateRangeLabel(dateRange);
  }, [dateRange, dateRangeCustomFrom, dateRangeCustomTo]);

  const handlePickPreset = useCallback(
    (p: StatsDateRangePreset) => {
      if (p === 'custom') return;
      const range = getRangeFromPreset(p);
      if (!range) return;
      onCommit({
        dateRange: p,
        dateRangeCustomFrom: undefined,
        dateRangeCustomTo: undefined,
      });
      setOpen(false);
    },
    [onCommit],
  );

  const handleApply = useCallback(() => {
    const nextPreset = getPresetFromRange(localFrom, localTo);
    if (nextPreset !== 'custom' && (!presets || presets.includes(nextPreset))) {
      onCommit({
        dateRange: nextPreset,
        dateRangeCustomFrom: undefined,
        dateRangeCustomTo: undefined,
      });
    } else {
      onCommit({
        dateRange: 'custom',
        dateRangeCustomFrom: localFrom,
        dateRangeCustomTo: localTo,
      });
    }
    setOpen(false);
  }, [localFrom, localTo, onCommit, presets]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          variant="outline"
          size="sm"
          type="button"
          className="flex h-7 w-fit min-w-0 items-center justify-between gap-1 px-2"
          data-testid="date-range-preset-trigger"
        >
          <span className="whitespace-nowrap text-[11px]">{display}</span>
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 w-max min-w-[140px] rounded-md border border-border bg-popover py-1.5 text-popover-foreground shadow-md"
        >
          <div className="max-h-[220px] overflow-y-auto px-1">
            {PRESETS.map((p) => {
              const active = dateRange !== 'custom' && dateRange === p.code;
              return (
                <button
                  key={p.code}
                  type="button"
                  className={cn(
                    'flex w-full items-center rounded px-2 py-1.5 text-left text-xs transition-colors',
                    active ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50',
                  )}
                  onClick={() => handlePickPreset(p.code)}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <div className="my-1 border-t border-border" />
          <div className="px-2 pb-1.5">
            <div className="mb-1 flex flex-col gap-1">
              <input
                type="date"
                value={localFrom}
                onChange={(e) => setLocalFrom(e.target.value)}
                className="h-7 w-full min-w-0 rounded border border-input bg-background px-2 text-xs"
              />
              <input
                type="date"
                value={localTo}
                onChange={(e) => setLocalTo(e.target.value)}
                className="h-7 w-full min-w-0 rounded border border-input bg-background px-2 text-xs"
              />
            </div>
            <Button variant="ghost" size="sm" type="button" className="h-7 w-full text-xs" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default FormulaGroupDateRangeDropdown;
